import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, GripVertical } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

function Slider({ id, antes, depois, altAntes, altDepois, nome }) {
  const ref = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(50);

  const update = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(100, Math.max(0, pct)));
  };

  const onDown = (e) => {
    dragging.current = true;
    ref.current.setPointerCapture(e.pointerId);
    update(e.clientX);
  };
  const onMove = (e) => {
    if (dragging.current) update(e.clientX);
  };
  const onUp = () => {
    dragging.current = false;
  };

  return (
    <div
      className="snap-start shrink-0 w-[88%] sm:w-[70%] lg:w-[calc(50%-12px)] rounded-[28px] border border-white/10 bg-[#131313] p-4 md:p-5"
      data-testid={`before-after-card-${id}`}
    >
      <div
        className="relative touch-pan-y select-none overflow-hidden rounded-2xl"
        ref={ref}
        data-testid={`before-after-slider-${id}`}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
      >
        {/* DEPOIS (base) */}
        <img
          src={depois}
          alt={altDepois}
          draggable="false"
          className="pointer-events-none block aspect-[3/5] w-full object-cover"
        />
        {/* ANTES (recortado por cima) */}
        <img
          src={antes}
          alt={altAntes}
          draggable="false"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        />

        {/* Etiquetas */}
        <span className="absolute left-3 top-3 z-20 rounded-full bg-black/75 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white backdrop-blur-sm">
          Antes
        </span>
        <span className="absolute right-3 top-3 z-20 rounded-full bg-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-black">
          Depois
        </span>

        {/* Linha + alça */}
        <div
          className="absolute inset-y-0 z-20 w-px bg-white"
          style={{ left: `${pos}%` }}
        >
          <span
            data-testid={`before-after-handle-${id}`}
            className="absolute left-1/2 top-1/2 grid h-11 w-11 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize place-items-center rounded-full bg-white text-black shadow-[0_0_25px_rgba(255,255,255,0.45)]"
          >
            <GripVertical className="h-5 w-5" />
          </span>
        </div>
      </div>
      <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
        {nome}
      </p>
    </div>
  );
}

export default function BeforeAfter() {
  return (
    <section id="evolucao" className="section-pad border-t border-white/10">
      <div className="container-x">
        <motion.div {...reveal} className="text-center">
          <p className="overline-label">Evolução real</p>
          <h2 className="h2-display mt-4 text-white">
            Antes &amp; <span className="text-stroke">depois.</span>
          </h2>
          <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-white/55 md:text-base">
            Veja a evolução com treino e dieta acompanhados de perto.
          </p>
        </motion.div>

        <motion.p
          {...reveal}
          className="mt-10 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-white/40"
        >
          Arraste
          <ArrowRight className="h-4 w-4" />
        </motion.p>

        <motion.div
          {...reveal}
          className="no-scrollbar mt-4 flex snap-x snap-mandatory gap-6 overflow-x-auto pb-2"
          data-testid="before-after-carousel"
        >
          <Slider
            id="lucas"
            antes="/antes.jpg"
            depois="/depois.jpg"
            altAntes="Lucas Fucht antes da consultoria"
            altDepois="Lucas Fucht depois da consultoria"
            nome="Lucas Fucht"
          />
          <Slider
            id="tiago"
            antes="/tiago-antes.jpg"
            depois="/tiago-depois.jpg"
            altAntes="Tiago antes da consultoria"
            altDepois="Tiago depois da consultoria"
            nome="Tiago Beppler"
          />
        </motion.div>

        <motion.p
          {...reveal}
          className="mt-8 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-white/40"
        >
          Resultados variam de pessoa para pessoa — os seus também serão
          acompanhados de perto
        </motion.p>
      </div>
    </section>
  );
}

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};
